import { faTimesCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Dialog,
	DialogContent,
	Grid,
	IconButton,
	Typography,
	CircularProgress,
	Alert,
	DialogTitle,
	Skeleton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useState } from 'react';
import ResourceCard from './ResourceCard';
import { isMobile } from 'react-device-detect';
import { LanguageContext } from '../../context/LanguageContext';
import { strapiRequest } from '../../functions/axiosRequest';
import CropSelect from './CropSelect';
import TypeSelect from './TypeSelect';
import CategorySelect from './CategorySelect';

const useStyles = makeStyles(theme => ({
	dialog: {
		'& .MuiBackdrop-root': {
			backgroundColor: 'rgba(250,250,250, 0.6)'
		},
		'& .MuiDialog-paper': {
			height: isMobile ? 'auto' : 'calc(100% - 64px)'
		},
		'& .MuiDialogContent-root': {
			background: theme.palette.grey[100],
			overflowY: 'auto',
			padding: theme.spacing(isMobile ? 2 : 5),
			'& #dialog_content': {
				position: 'relative',
				padding: theme.spacing(isMobile ? 1 : 3),
				border: isMobile ? 'none' : `3px solid ${theme.palette.common.white}`,
				'& .MuiIconButton-root': {
					position: 'absolute',
					top: -34,
					right: -34,
					color: theme.palette.resources.main,
					'& svg': {
						fontSize: '2.5rem',
						'& .fa-secondary': {
							color: theme.palette.resources.main,
							opacity: 1
						},
						'& .fa-primary': {
							color: 'white'
						}
					}
				}
			}
		}
	},
	title: {
		background: theme.palette.resources.main,
		padding: theme.spacing(2),
		color: theme.palette.common.white,
		marginBottom: theme.spacing(1),
		fontWeight: 400
	},

	subtitle: {
		fontWeight: 400
	}
}));

const ResourceDialog = ({ open, handleClose }) => {

	const locale=useContext(LanguageContext);
	const classes = useStyles();

	const [selectedCrop, setSelectedCrop] = useState(null);
	const [selectedCategory, setSelectedCategory]=useState(null);
	const [selectedType, setSelectedType] = useState(null);
	const [resources, setResources] = useState(null);
	const [filteredResources, setFilteredResources]=useState(null);

	//Fetch resources on crop and category change
	useEffect(()=>{
		if(selectedCrop && selectedCategory){
			const params = {
				locale: locale.lang.code,
				sort: 'createdAt:asc',
				'filters[crop][documentId][$eq]': selectedCrop.documentId
			};

			if(selectedCategory.documentId !== 'all'){
				params['filters[product_category][documentId][$eq]'] = selectedCategory.documentId;
			}

			strapiRequest({
				method: 'get',
				endpoint: 'resources',
				params
			}).then((response)=>{
				setResources(response.data);
			}).catch((error)=>{
				console.log(error);
			});
		}
	},[selectedCrop, selectedCategory, locale.lang.code]);

	//Filter resources on resource type change
	useEffect(()=>{
		if(selectedType && resources){
			//console.log(selectedType)
			if(selectedType.documentId==='all'){
				setFilteredResources(resources);
				
			}
			else{
				const filtered=resources.filter((resource)=>resource.type?.[0]?.__component===selectedType.component);
				setFilteredResources(filtered);
			}
		}
	},[selectedType, resources]);

	//console.log(filteredResources)

	//Fetch text & translations
	const [textData, setTextData]=useState();
	useEffect(()=>{
		strapiRequest({
			method: 'get',
			endpoint: 'misc-text-areas',
			params: { locale: locale.lang.code }
		}).then((response)=>{
			if(response.data){
				setTextData(response.data);
			}
		}).catch((error)=>{
			console.log(error);
		});
	},[locale]);

	//console.log(textData)

	return(
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="resources_dialog"
			className={classes.dialog}
			fullWidth
			maxWidth="xl"
		>
			{isMobile &&
				<DialogTitle>
					{textData? textData.repository_heading : <Skeleton/>}
					{handleClose?
						<IconButton
							aria-label="close"
							onClick={handleClose}
							sx={{
								position: 'absolute',
								right: 8,
								top: 8,
								color: theme => theme.palette.resources.main,
							}}
						>
							<FontAwesomeIcon icon={faTimesCircle} size='xl' />
						</IconButton>
					: 
						null
					}
				</DialogTitle>
			}
			<DialogContent>
				<Grid
					container
					spacing={1}
					alignItems="center"
					direction="column"
					id="dialog_content"
				>
					{!isMobile ?
						<>
							<IconButton
								aria-label="close_dialog"
								onClick={handleClose}
								className={classes.closeIcon}
								col="resources"
							>
								<FontAwesomeIcon icon={faTimesCircle} />
							</IconButton>
							<Grid container item >
								<Grid item xs={12} textAlign="center" >
									<Typography variant={isMobile ? 'p' : 'h6'}
										sx={{
											background: theme => theme.palette.resources.main,
											padding: theme => theme.spacing(2),
											color: theme => theme.palette.common.white,
											margin: '0  auto 1rem auto',
											fontWeight: 400,
											width: 'fit-content'
										}}
									>
										{textData? textData.repository_heading : <Skeleton/>}
									</Typography>
								</Grid>
								<Grid item xs={12} textAlign="center" >
									<Typography variant={isMobile ? 'p' : 'h6'} sx={{ marginBottom: '1em' }}>
										{textData? textData.repository_subheading : <Skeleton/>}
									</Typography>
								</Grid>
							</Grid>
						</>
						: null}
					<Grid container spacing={2} item>
						<Grid item xs={4}>
							<CropSelect
								selectedCrop={selectedCrop}
								setSelectedCrop={setSelectedCrop}
								textData={textData}
							/>
						</Grid>
						<Grid item xs={4}>
							<CategorySelect
								selectedCategory={selectedCategory}
								setSelectedCategory={setSelectedCategory}
								textData={textData}
							/>
						</Grid>
						<Grid item xs={4}>
							<TypeSelect
								selectedType={selectedType}
								setSelectedType={setSelectedType}
								textData={textData}
							/>
						</Grid>
					</Grid>
					{filteredResources?
						filteredResources.length?
							<Grid container item spacing={2}
								sx={{
									marginTop:'0.5rem',
									padding:'1rem'
								}}
							>
								{filteredResources.map((resource)=>{
									if(resource.in_resources){
										return(
											<ResourceCard
												resource={resource}
												key={resource.documentId}
											/>
										);
									}
								})}
							</Grid>
						:
							<Grid item sx={{mt:'1rem'}}>
								<Alert severity="info">
									{textData?.repository_nonefound}
								</Alert>
							</Grid>
					:
						<CircularProgress/>
					}
				</Grid>
			</DialogContent>
		</Dialog >
	);
};

export default ResourceDialog;
